// ink-component
import React, { ReactNode, AnchorHTMLAttributes, RefObject } from 'react';
import styled, { ThemeProvider } from 'styled-components';

import getGlobalProps from '../../helpers/constants/global-props';
import useInkAnalytics from '../../hooks/useInkAnalytics';

import { anchorCore, anchorVariants, anchorSizes, anchorPresets } from './styles';

export type LinkPresetsType = 'new-tab' | 'forward' | 'backward' | 'download';

/*
  Notes:

  1. We should remove these props since they are used for Buttons not Anchors
    - `operators`
    - `isCell`
    - [type] `actions` (it doesn't even work in production)
    - [type] `large` no longer exist in Button so we should do the same here
      (see: https://github.com/carta/ink/pull/2980)

  2. We should rename `link` to `default` following the pattern in Button

  3. We should rename `btn` (points to Button `default`) to `button`

  4. We should rename `aura` for context clarity (explicit intent)

  5. We should check carta-web usage for `isBlock` to see if it's necessary

  6. We should add meaning to presets
*/

export interface AnchorProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  /** Unique identifier */
  id?: string;
  /** Contents */
  children?: ReactNode;
  /** An URL or a URL fragment that the link points to */
  href?: string;
  /** Size modifier */
  size?: 'mini' | 'small' | 'large' | 'wide';
  /** Type modifier. **DON'T** use `black` or `underline` yet! */
  type?: 'btn' | 'aura' | 'link' | 'danger' | 'actions' | 'primary' | 'inverse' | 'transparent' | 'black' | 'underline';
  /** Specifies where to display the linked URL */
  target?: string;
  /** Adds a preset arrow according to the link behavior */
  preset?: LinkPresetsType;
  /** Specifies the relationship of the target object to the link object */
  rel?: string;
  /** Operators modifier */
  operators?: 'plus' | 'minus';
  /** Cell modifier for tables, overwrites size modifier */
  isCell?: boolean;
  /** Block modifier, adds full width behavior. */
  isBlock?: boolean;
  /** Disabled state */
  isDisabled?: boolean;
  /** @ignore For Dropdown.Anchor */
  isDropdown?: boolean;
  /** Wraps the text for longer text when the type is 'link' */
  wrapText?: boolean;
  /** React ref object to be forwarded to the internal node */
  externalRef?: RefObject<HTMLAnchorElement>;
  /** Use Anchor styles with other components such as react-router's Link */
  as?: string | React.ComponentType<any>;
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;
  /** Unique name for analytics purposes. By default, components are not tracked, unless this is set */
  'data-track-name'?: string;
  /** Custom payload to be sent to analytics provider */
  'data-track-payload'?: any;
  /** List of tracked events in the analytics integration */
  'data-track-events'?: string[];
}

/*
  This function determines the rendering style for link elements based on specific requirements:

  - To transition away from black styles, that are now deprecated, both `black` and `underline` types render `link`.
  - When `type` is `link`:
    - If it's inside of a filled Tile, it renders as `underline`.
    - Otherwise, it defaults to `link` style.
  - All other types render as their own variant.
*/
export const renderLinkStyles = (type: string, tileVariant?: string) => {
  return tileVariant && tileVariant !== 'default' ? anchorVariants.underline : anchorVariants.link;
};

/*
  Styling notes:
  - `size` ONLY applies to `btn` types
*/
/* @internal */
export const StyledAnchor = styled.a`
  ${anchorCore};
  ${({ theme }) => anchorPresets[theme.preset]};

  /*
    These are basically Button references. We have to include the variant first
    because the size 'auto' is undefined in Anchor by default.
  */
  ${({ theme }) => (theme.isLink ? renderLinkStyles(theme.type, theme.tileVariant) : anchorVariants[theme.type])}
  ${({ theme }) => !theme.isLink && anchorSizes[theme.size]}
  ${({ theme }) => theme.isBlock && anchorSizes.block};
  ${({ theme }) => theme.isDropdown && anchorSizes.dropdown};
`;

const globalProps = getGlobalProps({});

/**
 * @purpose Links to different content
 * @categories accessory
 * @aka link
 * @HTMLType HTMLAnchorElement
 */
export function Anchor(props: AnchorProps) {
  const {
    className,
    href,
    isBlock,
    isCell,
    isDisabled,
    isDropdown,
    operators,
    preset,
    size,
    style,
    type,
    wrapText,
    externalRef,
    ...otherProps
  } = props;

  const [ref] = useInkAnalytics(props, { ref: externalRef });
  const isLink = type === 'link' || type === 'black' || type === 'underline';

  const to = typeof props.as !== 'string' ? props.href : undefined;

  return (
    <ThemeProvider
      theme={currentTheme => ({
        ...currentTheme,
        disabled: isDisabled,
        size: isCell ? 'cell' : size,
        isBlock,
        isDropdown,
        type,
        isLink,
        preset,
        /*
          wrapText is no longer doing anything, as we changed the
          white-space from nowrap to be normal by default.
          TODO: deprecate this prop
        */
        wrapText: type === 'link' && wrapText,
      })}
    >
      <StyledAnchor
        href={href}
        to={to}
        data-testid={props['data-testid']}
        {...globalProps}
        {...otherProps}
        ref={ref}
        aria-disabled={isDisabled}
        tabIndex={isDisabled ? -1 : 0}
      >
        {props.children}
      </StyledAnchor>
    </ThemeProvider>
  );
}

Anchor.defaultProps = {
  id: undefined,
  children: undefined,
  size: undefined,
  type: 'link',
  target: undefined,
  preset: undefined,
  rel: undefined,
  operators: undefined,
  isCell: false,
  isBlock: false,
  isDisabled: false,
  isDropdown: false,
  wrapText: false,
  'data-testid': undefined,
  'data-track-events': ['click'],
};

Anchor.displayName = 'Anchor';

export default Anchor;
